import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { STATUS_CODE_INVALID_SESSION, TOKEN_DATA_KEY, TOKEN_HEADER_KEY } from 'auth/src/lib/constants';
import { isEmpty, isNil } from 'lodash';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, take, tap } from 'rxjs/operators';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { BaseAuthService, TokenStorageService } from '../services';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private httpErrorSubject: BehaviorSubject<HttpErrorResponse> = new BehaviorSubject<HttpErrorResponse>(null);
    private _invalidSessionResponse = new HttpErrorResponse({
        error: { message: 'Generic.suaSessaoExpirou' },
        statusText: 'TOKEN_EXPIRED OR INVALID_TOKEN',
        status: STATUS_CODE_INVALID_SESSION
    });

    constructor(private _tokenService: TokenStorageService, private authService: BaseAuthService) {

        this.httpErrorSubject.pipe(
            filter(error => error !== null),
            debounceTime(500),
        ).subscribe(
            error => new Observable(_ => {
                throw error;
            }).subscribe()
        );

    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<Object>> {
        let authReq = req;

        const token = this._tokenService.getToken();
        const isAuthenticated = !!token;
        const hasToken = !isNil(token) && !isEmpty(token);


        if (hasToken) {
            authReq = this.addTokenHeader(req, token);
        }

        return next.handle(authReq)
            .pipe(
                tap(() => {

                    if (!isNil(token)) {
                        const resultParseJwt = this._parseJwt(token);
                        const claimRole = Object.keys(resultParseJwt).find(claim => claim.includes('role'));
                        const jwtRole = resultParseJwt[claimRole];

                        const authData = localStorage.getItem(TOKEN_DATA_KEY);
                        const authDataParsed = JSON.parse(authData);
                        const localStorageRole = authDataParsed
                            ? (authDataParsed.role
                                ? authDataParsed.role
                                : authDataParsed.roles)
                            : null;

                        const NotImpersonatedUser = isEmpty(localStorage.getItem('ImpersonatedUser'));
                        const NotSeeHowRole = isEmpty(localStorage.getItem('SeeHowRole'));

                        setTimeout(() => {
                            if (!isNil(localStorageRole) && JSON.stringify(jwtRole) !== JSON.stringify(localStorageRole) && NotImpersonatedUser && NotSeeHowRole) {
                                this.authService.logout({ redirect: false });
                            }
                        }, 200);
                    }
                }),
                tap(() => {
                    if (!this._tokenService.hasSavedAuthentication() && isAuthenticated) {
                        this._tokenService.renewTokenTimeExpiration();
                    } else {
                        this._tokenService.clearStorageKeys({
                            keysToOmit: [
                                LocalStorageService.key.version,
                                LocalStorageService.key.darkMode,
                            ]
                        });
                    }
                }),
                catchError((response: HttpErrorResponse) => {
                    const isUnauthorized = response.status === 401;
                    if (token && response instanceof HttpErrorResponse && !authReq.url.includes('login') && isUnauthorized) {
                        return this.handle401Error(authReq, next);
                    }

                    if (isUnauthorized || response.status === STATUS_CODE_INVALID_SESSION) {
                        this.httpErrorSubject.next(this._invalidSessionResponse);
                    }

                    return isUnauthorized ? EMPTY : throwError(response);
                })
            );
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(null);

            const token = this._tokenService.getRefreshToken();

            if (!token) this.httpErrorSubject.next(this._invalidSessionResponse);

            return this.authService.refreshToken(token).pipe(
                switchMap(response => {
                    this.isRefreshing = false;
                    const authData = response.data;
                    const token = authData.accessToken;

                    this._tokenService.saveToken(authData);
                    this.refreshTokenSubject.next(token);

                    return next.handle(this.addTokenHeader(request, token));
                }),
                catchError((err) => {
                    this.isRefreshing = false;

                    this.httpErrorSubject.next(this._invalidSessionResponse);
                    return EMPTY;
                })
            );
        }

        return this.refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap((token) => next.handle(this.addTokenHeader(request, token)))
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        token = token.replace('Bearer ', '');
        return request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, `Bearer ${token}`) });
    }

    private _parseJwt(token: string) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

}

